import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  Button,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/ui';
import type { AccountingOperationDto } from '@shared/api/contracts';
import { formatSignedAmount, formatAmount } from '@shared/lib/format-amount';
import { formatDateTime } from '@shared/lib/format-date';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';

interface PartnersTableProps {
  operations: AccountingOperationDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  isLoading?: boolean;
  error?: unknown;
  onRefresh?: () => void;
  onPageChange?: (page: number) => void;
}

export function PartnersTable({
  operations,
  pagination,
  isLoading = false,
  error,
  onRefresh,
  onPageChange,
}: PartnersTableProps) {
  const { t } = useTranslation('cabinet');

  return (
    <div className="surface-card">
      <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
        <h2 className="section-title">{t('referral.table.heading')}</h2>
        <Button
          variant="outline"
          size="icon-sm"
          className="rounded-full sm:size-10"
          onClick={onRefresh}
          loading={isLoading}
          leftIcon={<RefreshCw className="size-4" />}
          aria-label={t('common.retry', { ns: 'common' })}
        />
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center sm:py-12">
          <p className="font-sans text-sm text-destructive">
            {getApiErrorMessage(error, t, 'referral.errors.tableLoad')}
          </p>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            {t('common.retry', { ns: 'common' })}
          </Button>
        </div>
      ) : isLoading && !operations.length ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : !operations.length ? (
        <div className="py-12 text-center font-sans text-xs text-muted-foreground sm:py-16 sm:text-sm">
          {t('referral.table.empty')}
        </div>
      ) : (
        <>
          <div className="-mx-1 min-w-0 px-1">
            <Table className="table-compact">
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">{t('balance.columns.amount')}</TableHead>
                <TableHead className="text-right">{t('referral.table.balance')}</TableHead>
                <TableHead>{t('balance.columns.date')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operations.map((operation, index) => {
                const sum = Number(operation.sum);
                const isNegative = sum < 0;

                return (
                  <TableRow key={`${operation.createdAt}-${index}`}>
                    <TableCell
                      className={`text-right font-medium ${
                        isNegative ? 'text-fail-foreground' : 'text-success-foreground'
                      }`}
                    >
                      {formatSignedAmount(sum)} $
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatAmount(operation.balance_amount)} $
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(operation.createdAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>

          <Pagination
            page={pagination.page}
            limit={pagination.limit}
            total={pagination.total}
            onPageChange={(page) => onPageChange?.(page)}
          />
        </>
      )}
    </div>
  );
}
